import { DOCUMENT } from '@angular/common';
import {
  DestroyRef,
  Injectable,
  InjectionToken,
  Signal,
  inject,
  signal
} from '@angular/core';
import { datadogRum } from '@datadog/browser-rum';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable, from } from 'rxjs';
import { authService as viveAuthService } from 'src/app/shared/vive-api/auth.service';
import { SessionDto } from 'src/app/shared/vive-api/vive-api-models';
import { ViveApiError } from 'src/app/shared/vive-api/vive-api.error';

import { vendorIdFromSession } from '../../vendor-redirect';
import { User } from '../classes/models';
import { AppSession, toAppSession } from '../interfaces/app-session';
import { UserAccountTypes } from '../interfaces/user-account-types';
import { LoginModalComponent } from '../modals/login-modal/login-modal.component';
import { PortalRoutingService } from './portal-routing.service';
import { PosthogIdentityService } from './posthog-identity.service';

declare const pendo: any;
declare const chmln: any;

/**
 * The session `main.ts` read from `GET /api/v3/auth/session` before bootstrap —
 * the signed-in user, or null for an anonymous visitor and when the load gave
 * up (`SESSION_UNAVAILABLE` tells the two apart). `main.ts` provides it on the
 * platform injector, so AppModule declares no provider for it.
 */
export const INITIAL_SESSION = new InjectionToken<AppSession | null>(
  'INITIAL_SESSION'
);

/**
 * True when `main.ts` gave up asking `GET /api/v3/auth/session` without a
 * session or a 401, so who is signed in is still unknown. Provided beside
 * `INITIAL_SESSION` on the platform injector.
 */
export const SESSION_UNAVAILABLE = new InjectionToken<boolean>(
  'SESSION_UNAVAILABLE'
);

const SESSION_RETRY_TIMEOUT_MS = 8000;

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  /**
   * The signed-in user as the API last described it. The credential is an
   * HttpOnly cookie on the API host that this app never sees, so every
   * synchronous getter reads this instead.
   */
  private session: AppSession | null =
    inject(INITIAL_SESSION, { optional: true }) ?? null;
  private readonly unavailable = signal(
    inject(SESSION_UNAVAILABLE, { optional: true }) ?? false
  );
  /** True until the API answers the session check with a session or a 401. */
  public readonly sessionUnavailable: Signal<boolean> =
    this.unavailable.asReadonly();
  private document = inject(DOCUMENT);
  private destroyRef = inject(DestroyRef);
  private watchingBrowserSession = false;

  constructor(
    private ModalService: NgbModal,
    private portalRoutingService: PortalRoutingService,
    private posthogIdentityService: PosthogIdentityService
  ) {}

  private handleReAuthentication(fnToCallOnSuccess: Function) {
    // Clear open modals, display re-login modal.
    if (this.ModalService.hasOpenModals()) {
      this.ModalService.dismissAll();
    }
    this.ModalService.open(LoginModalComponent, {
      backdrop: 'static'
    }).result.then(() => fnToCallOnSuccess());
  }

  private requireUser(): User {
    if (!this.session) {
      throw new Error('Error. User not authenticated.');
    }
    return this.session.user;
  }

  public getUser(): User {
    return this.requireUser();
  }

  public getUserId(): number {
    return this.requireUser().id as number;
  }

  public getUserCompanyId(): number {
    return this.requireUser().CompanyId as number;
  }

  public getUserVendorId(): number {
    return this.requireUser().VendorId as number;
  }

  public getUserType(): Observable<UserAccountTypes> {
    const observable = new Observable<UserAccountTypes>((subscriber) => {
      const retrieveUserType = (): void => {
        subscriber.next(this.requireUser().UserAccountType!.name);
        subscriber.complete();
      };
      if (!this.isAuthenticated()) {
        this.handleReAuthentication(retrieveUserType);
      } else {
        retrieveUserType();
      }
    });
    return observable;
  }

  // User needs to be authenticated for this to work properly. Otherwise, use getUserType.
  public isClient(): boolean {
    return this.requireUser().UserAccountType?.name === 'Client';
  }

  // User needs to be authenticated for this to work properly. Otherwise, use getUserType.
  public isVendor(): boolean {
    return this.requireUser().UserAccountType?.name === 'Vendor';
  }

  /**
   * True once a session is seeded. Expiry is the server's call: a session that
   * lapsed after it was seeded comes back as a 401, which the HTTP interceptor
   * answers with the LoginModal.
   */
  public isAuthenticated(): boolean {
    return this.session !== null;
  }

  /** The seeded session as the API described it, or null when nobody is signed in or it is still unknown. */
  public getSession(): SessionDto | null {
    return this.session;
  }

  /** Decided by the server's session record, never by a JWT claim. */
  public isImpersonating(): boolean {
    return this.session?.session.impersonation != null;
  }

  /** The vendor the vive-vendors redirect should consider, or null when this session is never redirected. */
  public getRedirectableVendorId(): number | null {
    return vendorIdFromSession(this.session);
  }

  /**
   * Forget the session in this tab and reset analytics identity. It does not
   * revoke the cookie (the logout page's DELETE /auth/session is the sign-out),
   * so the next page load re-seeds from the server.
   */
  public clearSession(): void {
    this.session = null;
    this.portalRoutingService.reset();
    try {
      datadogRum.clearUser();
    } catch (e) {
      console.error('DataDog RUM clearUser failed:', e);
    }
    // Reset PostHog so the next session starts a fresh anonymous id.
    this.posthogIdentityService.reset();
  }

  /**
   * Identify the seeded user to Pendo, Chameleon, Datadog RUM and PostHog.
   * AppModule runs it once at bootstrap; `seedSession()` runs it for every
   * session that arrives after that.
   */
  public identify(): void {
    if (!this.session) {
      return;
    }
    const { user } = this.session;
    const account = user.Company || user.Vendor;
    const accountType = user.UserAccountType?.name;

    try {
      pendo.initialize({
        visitor: {
          id: user.id,
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          userRole: user.userRole
        },
        account: {
          id: account?.id,
          accountName: account?.legalName,
          payingStatus:
            accountType === 'Client' ? (account as any)?.package : null,
          isVendor: accountType === 'Vendor' ? (account as any)?.isExempt : null
        }
      });
    } catch (e) {
      console.error('Pendo initialization failed:', e);
    }

    // VIVE admin users and impersonation sessions are excluded from Chameleon
    // so they don't count toward Monthly Active Users.
    if (accountType !== 'Admin' && !this.isImpersonating()) {
      try {
        // Chameleon uses (userId, properties); company is omitted when the user
        // has no associated account (uid must never be null).
        chmln.identify(user.id, {
          uid_hash: user.chameleonUidHash,
          email: user.email,
          name: [user.firstName, user.lastName].filter(Boolean).join(' '),
          firstName: user.firstName,
          lastName: user.lastName,
          jobTitle: user.jobTitle,
          role: user.userRole,
          createdAt: user.createdAt,
          ...(account?.id
            ? {
                company: {
                  uid: account.id,
                  name: account.legalName,
                  accountType
                }
              }
            : {})
        });
      } catch (e) {
        console.error('Chameleon identify failed:', e);
      }
    }

    // DataDog RUM identifies ALL authenticated users — including VIVE admins and
    // impersonation sessions — unlike Pendo/Chameleon above. No-op until
    // datadogRum.init() has run (gated on the datadog env config in main.ts).
    try {
      datadogRum.setUser({
        id: String(user.id),
        name: [user.firstName, user.lastName].filter(Boolean).join(' '),
        email: user.email,
        accountType,
        role: user.userRole,
        ...(account?.id ? { accountId: account.id } : {})
      });
    } catch (e) {
      console.error('DataDog RUM setUser failed:', e);
    }

    // Attribute PostHog events to this user (skips admins/impersonation and
    // dedupes unchanged users internally). Self-guards if PostHog is unconfigured.
    this.posthogIdentityService.syncUser(user, this.isImpersonating());
  }

  /**
   * Replace this tab's session with one the API just returned — a sign-in, a
   * Connect exchange, or an impersonation start or Return — and identify it.
   * Bootstrap does not come through here: `INITIAL_SESSION` seeds the field.
   */
  public seedSession(session: SessionDto): void {
    this.session = toAppSession(session);
    // A new session starts: drop any portal-routing decision cached for the
    // previous user so the redirect guard re-fetches for this one.
    this.portalRoutingService.reset();
    this.identify();
  }

  /**
   * Ask the API once more who is signed in, for the can't-reach page. A
   * session or a 401 settles the question; anything else leaves the session
   * unavailable for the next try. Silent: no toast, no loading mask, and a 401
   * here never opens the LoginModal because `/auth/session` is a credential
   * route.
   */
  public async retrySessionCheck(): Promise<void> {
    try {
      this.seedSession(
        await viveAuthService.getSession({
          options: {
            requestTimeout: SESSION_RETRY_TIMEOUT_MS,
            context: { ManuallyHandleError: true, SkipLoadingMask: true }
          }
        })
      );
    } catch (error) {
      if (!(error instanceof ViveApiError && error.httpStatusCode === 401)) {
        return;
      }
    }
    this.unavailable.set(false);
  }

  /**
   * Reload at the login page for a tab whose identity changed underneath it:
   * every cached page and service belongs to the old one. Bootstrap re-reads
   * the browser's session, and the login page sends a signed-in user to their
   * landing page or shows the form.
   */
  public restartOnBrowserSession(): void {
    this.document.location.assign('/login');
  }

  /**
   * Another tab or app can change the browser's session (a sign-in as someone
   * else, an impersonation start, a Return), and this tab would go on showing
   * the old identity while its requests act as the new one. Re-read the
   * session whenever the tab comes back into view.
   */
  public watchBrowserSession(): void {
    if (this.watchingBrowserSession) {
      return;
    }
    this.watchingBrowserSession = true;

    const onVisibilityChange = (): void => {
      if (this.document.visibilityState === 'visible') {
        this.recheckBrowserSession();
      }
    };
    this.document.addEventListener('visibilitychange', onVisibilityChange);
    this.destroyRef.onDestroy(() =>
      this.document.removeEventListener('visibilitychange', onVisibilityChange)
    );
  }

  private recheckBrowserSession(): void {
    if (!this.session) {
      // An anonymous tab: the login page owns signing in.
      return;
    }
    from(
      viveAuthService.getSession({
        options: {
          context: { ManuallyHandleError: true, SkipLoadingMask: true }
        }
      })
    ).subscribe({
      next: (browserSession) => {
        // Compared on arrival: a start or Return in this tab while the
        // request was out has already re-seeded `this.session`.
        if (
          this.session &&
          sessionIdentity(browserSession) !== sessionIdentity(this.session)
        ) {
          this.restartOnBrowserSession();
        }
      },
      // Signed out elsewhere (401): this tab's next API call opens the
      // LoginModal. Offline or an API error: keep what the tab has.
      error: () => undefined
    });
  }
}

/** Who a session acts as. A change means another tab or app swapped the browser's identity. */
function sessionIdentity({ user, session }: SessionDto): string {
  return [
    user.id,
    user.CompanyId ?? '',
    session.impersonation?.impersonatorUserId ?? ''
  ].join(':');
}
